import React, { Component } from 'react';
import { 
    TextField, 
    Button, 
    List, 
    ListItem, 
    ListItemIcon, 
    ListItemText,
    Paper,
    CircularProgress,
    Typography,
    Box
} from '@mui/material';
import {
    CallSplit,  // For intersections
    Home,       // For street addresses
    LocalParking, // For parking lots
    Place,      // For other locations
    Business,   // For businesses (to show they're filtered)
    MyLocation  // For GPS location
} from '@mui/icons-material';

  // At the top of Autocomplete.js, add the import:
import { logAnalyticsEvent } from '../../utils/analytics';
class AutoComplete extends Component {
    constructor(props) {
        super(props);
        this.state = {
            useGPS: false,
            latitude: props.lat || null, 
            longitude: props.lng || null,
            predictions: [],
            showDropdown: false,
            selectedText: '',
            isLoading: false,
            sessionToken: null
        };
        this.clearSearchBox = this.clearSearchBox.bind(this);
        this.autocompleteService = null;
        this.placesService = null;
        this.searchTimeout = null;
        this.dropdownRef = React.createRef();
    }

    componentDidMount() {
        this.initializeServices();
        // Add click outside listener
        document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lat !== this.props.lat || prevProps.lng !== this.props.lng) {
            this.setState({ 
                latitude: this.props.lat,
                longitude: this.props.lng
            });
        }

        if (!prevProps.mapApi && this.props.mapApi && !this.autocompleteService) {
            this.initializeServices();
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
    }

    handleClickOutside = (event) => {
        if (this.dropdownRef.current && !this.dropdownRef.current.contains(event.target) &&
            this.searchInput && !this.searchInput.contains(event.target)) {
            this.setState({ showDropdown: false });
        }
    };

    initializeServices = () => {
        const { mapApi } = this.props;
        
        if (!mapApi || !mapApi.places) {
            console.log('Google Maps API not ready for autocomplete');
            return;
        }

        try {
            // Initialize AutocompleteService for more control
            this.autocompleteService = new mapApi.places.AutocompleteService();
            this.placesService = new mapApi.places.PlacesService(document.createElement('div'));
            
            // Create new session token for billing optimization
            this.setState({ sessionToken: new mapApi.places.AutocompleteSessionToken() });
            
            console.log('Autocomplete services initialized successfully');
        } catch (error) {
            console.error('Error initializing autocomplete services:', error);
        }
    };

    // Determine place type and get appropriate icon
    getPlaceTypeAndIcon = (place) => {
        const types = place.types || [];
        const description = place.description || '';
        const terms = place.terms || [];
        
        // Check if it's an intersection
        const isIntersection = 
            types.includes('intersection') ||
            description.includes(' & ') ||
            description.includes(' and ') ||
            (terms.length >= 2 && terms[0].value.includes('&'));
        
        if (isIntersection) {
            return { type: 'intersection', icon: <CallSplit />, priority: 1 };
        }
        
        // Check if it's a business (to filter out)
        const businessTypes = ['establishment', 'store', 'restaurant', 'cafe', 'bar', 
                              'bank', 'hospital', 'pharmacy', 'shopping_mall', 
                              'supermarket', 'gas_station', 'gym', 'spa'];
        const isBusiness = types.some(type => businessTypes.includes(type));
        
        if (isBusiness) {
            return { type: 'business', icon: <Business />, priority: 5, filtered: true };
        }
        
        // Check for parking lots
        if (types.includes('parking')) {
            return { type: 'parking', icon: <LocalParking />, priority: 3 };
        }
        
        // Check for street addresses
        if (types.includes('street_address') || types.includes('route')) {
            return { type: 'address', icon: <Home />, priority: 2 };
        }
        
        // Other location types
        return { type: 'other', icon: <Place />, priority: 4 };
    };

    handleAddressSearch = (value) => {
        if (!this.autocompleteService || !value) {
            this.setState({ predictions: [], showDropdown: false });
            return;
        }

        // Clear previous timeout
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        // Debounce search
        this.searchTimeout = setTimeout(() => {
            this.setState({ isLoading: true });

            const request = {
                input: value,
                componentRestrictions: { country: 'us' },
                sessionToken: this.state.sessionToken,
                // Request all types to get intersections
                types: []
            };

            // Add location bias if GPS coordinates are available
            if (this.state.latitude && this.state.longitude) {
                request.location = new window.google.maps.LatLng(this.state.latitude, this.state.longitude);
                request.radius = 50000; // 50km radius
            }

            this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
                    // Process and filter predictions
                    const processedPredictions = predictions
                        .map(prediction => {
                            const placeInfo = this.getPlaceTypeAndIcon(prediction);
                            return {
                                ...prediction,
                                ...placeInfo
                            };
                        })
                        // Filter out businesses
                        .filter(pred => !pred.filtered)
                        // Sort by priority (intersections first)
                        .sort((a, b) => a.priority - b.priority);

                    this.setState({
                        predictions: processedPredictions,
                        showDropdown: true,
                        isLoading: false
                    });
                } else {
                    this.setState({
                        predictions: [],
                        showDropdown: false,
                        isLoading: false
                    });
                }
            });
        }, 300); // 300ms debounce
    };


// Then update your existing handlePlaceSelect function:
handlePlaceSelect = (prediction) => {
    const { addplace, fetchAddressOnPointerChange } = this.props;
    
    // Track autocomplete selection RIGHT AT THE START
    logAnalyticsEvent('autocomplete_place_selected', {
        placeType: prediction.type,
        hasCoordinates: !!prediction.place_id,
        isIntersection: prediction.type === 'intersection'
    });
    
    // Store the selected text
    const selectedText = prediction.description;
    this.setState({ 
        selectedText: selectedText,
        showDropdown: false,
        predictions: []
    });

    // Handle intersection specially
    if (prediction.type === 'intersection') {
        this.handleIntersectionSelect(prediction, selectedText);
    } else {
        // For regular addresses, use original behavior
        const request = {
            placeId: prediction.place_id,
            fields: ['geometry', 'address_components', 'formatted_address', 'name'],
            sessionToken: this.state.sessionToken
        };

        this.placesService.getDetails(request, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                // Reset session token after successful place details request
                this.setState({ 
                    sessionToken: new window.google.maps.places.AutocompleteSessionToken() 
                });

                // Update the GPS coordinates in state
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                
                // ADD ANALYTICS TRACKING HERE - after getting coordinates
                logAnalyticsEvent('autocomplete_coordinates_updated', {
                    placeType: prediction.type,
                    latitude: lat.toFixed(6),
                    longitude: lng.toFixed(6),
                    previousLat: this.state.latitude ? this.state.latitude.toFixed(6) : 'none',
                    previousLng: this.state.longitude ? this.state.longitude.toFixed(6) : 'none'
                });
                
                this.setState({
                    latitude: lat,
                    longitude: lng
                });

                // Use addplace to update address from Places API response directly.
                // Do NOT call fetchAddressOnPointerChange here — it reverse-geocodes the
                // coordinates and can return business/neighborhood names (e.g. "Moore Therapy & Rehab")
                // which would overwrite the clean address from Places API.
                if (addplace && typeof addplace === 'function') {
                    addplace(place);
                }
            }
        });
    }
};

// Also update handleIntersectionSelect to include analytics:
handleIntersectionSelect = (prediction, selectedText) => {
    const { addplace, fetchAddressOnPointerChange } = this.props;
    
    // Extract ONLY the intersection name (everything before the first comma)
    const parts = selectedText.split(',');
    const intersectionName = parts[0].trim();
    
    // Update the address field directly with ONLY intersection name (no city, state, zip)
    this.props.onChangeHandler({ target: { value: intersectionName }}, 'addressLine');
    
    // Get place details for coordinates and other fields
    const request = {
        placeId: prediction.place_id,
        fields: ['geometry', 'address_components'],
        sessionToken: this.state.sessionToken
    };

    this.placesService.getDetails(request, (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            // Reset session token
            this.setState({ 
                sessionToken: new window.google.maps.places.AutocompleteSessionToken() 
            });

            // Update the GPS coordinates in state
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            
            // ADD ANALYTICS TRACKING FOR INTERSECTION
            logAnalyticsEvent('autocomplete_coordinates_updated', {
                placeType: 'intersection',
                latitude: lat.toFixed(6),
                longitude: lng.toFixed(6),
                previousLat: this.state.latitude ? this.state.latitude.toFixed(6) : 'none',
                previousLng: this.state.longitude ? this.state.longitude.toFixed(6) : 'none',
                intersectionName: intersectionName
            });
            
            this.setState({
                latitude: lat,
                longitude: lng
            });

            // Do NOT call fetchAddressOnPointerChange here — it reverse-geocodes the
            // coordinates and would overwrite the intersection address with a
            // business/neighborhood name from the reverse geocode result.

            // Create a custom place object for intersection
            const intersectionPlace = {
                ...place,
                formatted_address: intersectionName, // Only intersection name
                name: intersectionName,
                // Keep address components for city, state, zip to populate other fields
                address_components: place.address_components
            };

            if (addplace && typeof addplace === 'function') {
                addplace(intersectionPlace);
            }
        }
    });
};

    clearSearchBox() {
        if (this.searchInput) {
            this.searchInput.value = '';
            this.setState({ predictions: [], showDropdown: false });
        }
    }

    toggleUseGPS = () => {
        this.setState(prevState => {
            const newUseGPS = !prevState.useGPS;
            if (this.props.setIsGpsEnabled) {
                this.props.setIsGpsEnabled(newUseGPS);
            }
            return { useGPS: newUseGPS };
        });
    };

    handleGetGPS = () => {
        const options = {
            maximumAge: 0,
            timeout: 10000,
            enableHighAccuracy: true,
        };

        navigator.geolocation.getCurrentPosition(
            position => {
                const { latitude, longitude } = position.coords;

                this.setState({
                    latitude: latitude,
                    longitude: longitude,
                });

                if (this.props.fetchAddressOnPointerChange) {
                    this.props.fetchAddressOnPointerChange(latitude, longitude);
                }
            },
            error => {
                let errorMessage;
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "User denied the request for Geolocation.";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information is unavailable.";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "The request to get user location timed out.";
                        break;
                    case error.UNKNOWN_ERROR:
                    default:
                        errorMessage = "An unknown error occurred.";
                        break;
                }

                window.alert(errorMessage);
            },
            options
        );
    };

    handleCoordinateChange = (e) => {
        const { value } = e.target;
        const [lat, lng] = value.split(',').map(coord => parseFloat(coord.trim()));

        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            window.alert('Please enter valid coordinates.');
            return;
        }

        this.setState({ latitude: lat, longitude: lng });

        if (this.props.fetchAddressOnPointerChange) {
            this.props.fetchAddressOnPointerChange(lat, lng);
        }
    };

    render() {
        const { predictions, showDropdown, isLoading } = this.state;
        const isApiReady = !!this.props.mapApi;

        return (
            <Box sx={{ position: 'relative' }}>
                <TextField
                    className={`inputPadding`}  
                    fullWidth
                    label={"Latitude, Longitude"}
                    value={`${this.state.latitude || ''}, ${this.state.longitude || ''}`}
                    onChange={this.handleCoordinateChange}
                    sx={{ marginBottom: "15px !important" }}
                    placeholder="33.4484, -112.0740"
                    id="coordinates-input" 
                    type="text" 
                    variant="outlined"
                />
                
                <TextField
                    className={`inputPadding`}  
                    fullWidth
                    label={"Address"}
                    value={this.props.updatedValue || ''}
                    inputRef={(ref) => {
                        this.searchInput = ref;
                    }}
                    onChange={(e) => {
                        this.props.onChangeHandler(e, 'addressLine');
                        this.handleAddressSearch(e.target.value);
                    }}
                    sx={{ marginBottom: "15px !important" }}
                    placeholder="Enter an address or intersection..."
                    id="address-autocomplete" 
                    type="search" 
                    variant="outlined"
                    disabled={!isApiReady}
                    helperText={!isApiReady ? "Loading autocomplete..." : ""}
                    autoComplete="off"
                />

                {/* Custom Dropdown */}
                {showDropdown && predictions.length > 0 && (
                    <Paper 
                        ref={this.dropdownRef}
                        sx={{ 
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            zIndex: 1000,
                            maxHeight: 300,
                            overflow: 'auto',
                            mt: -1.5,
                            boxShadow: 3
                        }}
                    >
                        <List dense>
                            {predictions.map((prediction, index) => (
                                <ListItem
                                    key={prediction.place_id}
                                    button
                                    onClick={() => this.handlePlaceSelect(prediction)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                        borderBottom: index < predictions.length - 1 ? '1px solid #e0e0e0' : 'none'
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        {prediction.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" component="div">
                                                {prediction.structured_formatting?.main_text || prediction.description.split(',')[0]}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary">
                                                {prediction.structured_formatting?.secondary_text || 
                                                 prediction.description.split(',').slice(1).join(',')}
                                            </Typography>
                                        }
                                    />
                                    {prediction.type === 'intersection' && (
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                ml: 1, 
                                                px: 1, 
                                                py: 0.5, 
                                                backgroundColor: 'warning.light',
                                                borderRadius: 1,
                                                color: 'warning.dark'
                                            }}
                                        >
                                            Intersection
                                        </Typography>
                                    )}
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}

                {/* Loading indicator */}
                {isLoading && (
                    <Box sx={{ position: 'absolute', right: 10, top: 75 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
            </Box>
        );
    }
}

export default AutoComplete;